import { useEffect, useState, useRef, useCallback } from 'react';
import MenuMobileItem from './MobileMenuItem';
import { MENU_ITEMS } from '@constants/navi';
import { checkIsActive } from '@helpers/RouterHelpers';
import { useLocation } from 'react-router-dom';
import { toAbsoluteUrl } from '@helpers/index';

const MENU_ITEMS_REVERTED_LAST_TWO = [...MENU_ITEMS.slice(0, 3), MENU_ITEMS[4], MENU_ITEMS[3]];

export const MobileMenu = () => {
  const [activeItem, setActiveItem] = useState<number>(0);
  const [noActiveItem, setNoActiveItem] = useState<boolean>(false);
  const [activeItemOffset, setActiveItemOffset] = useState<number>(0);
  const [windowWidth, setWindowWidth] = useState<number>(window?.innerWidth || 0);
  const menuListRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  const { pathname } = useLocation();

  const handleItemClick = (index: number): void => {
    setActiveItem(index);
  };

  const calculateActiveItemOffset = useCallback(() => {
    if (menuListRef.current && itemRefs.current[activeItem]) {
      const menuListRect = menuListRef.current.getBoundingClientRect();
      const activeItemRect = itemRefs.current[activeItem]!.getBoundingClientRect();

      const leftBorderToItemCenter =
        activeItemRect.left - menuListRect.left + activeItemRect.width / 2;

      const menuListLeftOffset = menuListRect.left;

      const totalOffsetFromScreen = menuListLeftOffset + leftBorderToItemCenter;

      setActiveItemOffset(totalOffsetFromScreen);
    }
  }, [activeItem]);

  const updateWindowWidth = useCallback(() => {
    setWindowWidth(window.innerWidth);
  }, []);

  useEffect(() => {
    const activeIndex = MENU_ITEMS_REVERTED_LAST_TWO.findIndex((item) =>
      checkIsActive(pathname, item.to),
    );
    if (activeIndex !== -1) {
      setActiveItem(activeIndex);
      setNoActiveItem(false);
    } else {
      setNoActiveItem(true);
      setActiveItem(0);
    }
  }, [pathname]);

  useEffect(() => {
    calculateActiveItemOffset();

    const handleResize = () => {
      updateWindowWidth();
      calculateActiveItemOffset();
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    const timer = setTimeout(calculateActiveItemOffset, 50);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
      clearTimeout(timer);
    };
  }, [activeItem, calculateActiveItemOffset, updateWindowWidth]);

  const setItemRef = (index: number) => (el: HTMLDivElement | null) => {
    itemRefs.current[index] = el;
  };

  return (
    <div className="mobile-menu lg:hidden">
      <div className="relative flex items-end w-full">
        <div
          style={{
            transition: 'transform 0.3s ease',
            transform: `translateX(${activeItemOffset - 800}px)`,
            transformOrigin: 'center',
            width: '1600px',
            height: '105px',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            pointerEvents: 'none',
          }}>
          <img
            src={
              noActiveItem
                ? toAbsoluteUrl('/media/svg/header/cd-sticky-nav-bg-inactive.svg')
                : toAbsoluteUrl('/media/svg/header/cd-sticky-nav-bg.svg')
            }
            alt="nav background"
            style={{ width: '100%', height: '100%' }}
          />
        </div>
        <div className="mobile-menu-list" style={{ width: windowWidth }} ref={menuListRef}>
          {MENU_ITEMS_REVERTED_LAST_TWO.map((item, index) => {
            const isActive = checkIsActive(pathname, item.to);

            return (
              <MenuMobileItem
                key={item.id}
                ref={setItemRef(index)}
                item={item}
                isActive={isActive}
                onClick={() => handleItemClick(index)}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};
